import CheckPermission from "../../../hooks/CheckPermission";
import React, { useState } from "react";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { Dialog } from "primereact/dialog";
import AddTaskDialog from "../ward-management/AddTaskDialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import CustomButton from "../../../shared/CustomButton";
import { useSelector, useDispatch } from "react-redux";
import {
  addTask,
  updateTask,
  deleteTask,
} from "../../../redux/slices/WardManagementSlice";
import { Chip } from "primereact/chip";

const dummyWardPatients = [
  { id: 1, name: "John Doe", fileNo: "F001" },
  { id: 2, name: "Jane Smith", fileNo: "F002" },
];

const TasksTab = ({ wardId }) => {
  const { hasPermission } = CheckPermission();
  const dispatch = useDispatch();
  const tasks = useSelector((state) => state.wardManagement.tasks);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);

  // Add or update task
  const handleTaskSubmit = (task) => {
    if (editTask) {
      dispatch(updateTask({ ...task, id: editTask.id }));
      setEditTask(null);
    } else {
      dispatch(addTask(task));
    }
    setShowAddDialog(false);
  };

  // Edit
  const handleEdit = (task) => {
    if (!hasPermission('visit', 'update')) return;
    setEditTask(task);
    setShowAddDialog(true);
  };

  // Delete
  const handleDelete = (id) => {
    if (!hasPermission('visit', 'delete')) return;
    setDeleteTaskId(id);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    dispatch(deleteTask(deleteTaskId));
    setShowDeleteDialog(false);
    setDeleteTaskId(null);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 pb-4 flex-shrink-0 flex justify-between items-center">
        <h2 className="text-2xl font-bold">Tasks</h2>
        {hasPermission('visit', 'create') && (
          <CustomButton
            icon={<FaPlus />}
            label="Add Task"
            buttonType="primary"
            className="px-4 py-2"
            onClick={() => {
              setEditTask(null);
              setShowAddDialog(true);
            }}
          />
        )}
      </div>
      <div className="flex-1 px-6 pb-6 overflow-y-auto">
        {/* Task Cards */}
        <div className="space-y-4">
          {tasks.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No tasks added yet.
            </div>
          ) : (
            tasks.map((task) => (
              <div
                key={task.id}
                className="bg-white rounded-lg shadow p-4 flex flex-col md:flex-row md:items-center md:justify-between border border-gray-200"
              >
                <div className="flex-1">
                  <div className="font-semibold text-lg text-gray-800">
                    {task.title}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    {task.description}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="mt-auto">
                      Status:{" "}
                      <Chip
                        label={task.status}
                        className={`!text-white  ${
                          task.status === "Completed"
                            ? "bg-green-500"
                            : task.status === "Pending"
                            ? "bg-yellow-500"
                            : "bg-blue-500"
                        }`}
                      />
                    </span>
                    <span className="mt-auto">
                      Priority:{" "}
                      <Chip
                        label={task.priority}
                        className={`!text-white ${
                          task.priority === "High"
                            ? "bg-red-500"
                            : task.priority === "Medium"
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                      />
                    </span>
                    <span className="mt-auto">
                      Patient:{" "}
                      <span className="font-medium">
                        {dummyWardPatients.find(
                          (p) => p.id === Number(task.patientId)
                        )?.name || "-"}
                      </span>
                    </span>
                    <span className="mt-auto">
                      Date: <span className="font-medium">{task.date}</span>
                    </span>
                    <span className="mt-auto">
                      Due: <span className="font-medium">{task.dueDate}</span>
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 md:mt-0 md:ml-4">
                  <button
                    className="text-blue-600 hover:text-blue-800 p-2 rounded"
                    onClick={() => handleEdit(task)}
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800 p-2 rounded"
                    onClick={() => handleDelete(task.id)}
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Add/Edit Task Dialog */}
      <Dialog
        visible={showAddDialog}
        onHide={() => {
          setShowAddDialog(false);
          setEditTask(null);
        }}
        header={editTask ? "Edit Task" : "Add Task"}
        style={{ width: "600px" }}
        modal
        className="p-fluid"
      >
        <AddTaskDialog
          visible={true}
          onHide={() => {
            setShowAddDialog(false);
            setEditTask(null);
          }}
          wardId={wardId}
          wardPatients={dummyWardPatients}
          initialValues={editTask || undefined}
          onSubmit={handleTaskSubmit}
        />
      </Dialog>
      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        message="Are you sure you want to delete this task?"
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDelete}
        reject={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};

export default TasksTab;
